import { useCallback, useState, type DragEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  CloudUpload,
  Description,
  Error as ErrorIcon,
  HourglassBottom,
  OpenInNew,
} from "@mui/icons-material";
import { useIngestAttachments, useIngestUpload } from "@/hooks/useKBIngest";
import type { IngestAttachment, KBAttachmentProcessingStatus } from "@/types";

const STATUS_COLORS: Record<
  KBAttachmentProcessingStatus,
  "default" | "primary" | "success" | "error" | "warning"
> = {
  pending: "warning",
  processing: "primary",
  done: "success",
  failed: "error",
};

const STATUS_LABELS: Record<KBAttachmentProcessingStatus, string> = {
  pending: "Waiting",
  processing: "Processing",
  done: "Ready",
  failed: "Failed",
};

const ACCEPTED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "audio/mpeg",
  "audio/mp3",
  "audio/wav",
  "audio/ogg",
  "audio/flac",
  "audio/aac",
  "audio/m4a",
  "video/mp4",
  "video/mpeg",
  "video/quicktime",
  "video/webm",
  "text/plain",
  "text/markdown",
];

const ACCEPT_ATTRIBUTE = ACCEPTED_TYPES.join(",") + ",.md,.txt";
const MAX_BYTES = 20 * 1024 * 1024; // 20MB to match the backend's inline cap

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusChip({ status }: { status: KBAttachmentProcessingStatus }) {
  const inflight = status === "pending" || status === "processing";
  return (
    <Chip
      label={STATUS_LABELS[status]}
      color={STATUS_COLORS[status]}
      size="small"
      icon={inflight ? <HourglassBottom fontSize="small" /> : undefined}
      variant={status === "done" ? "filled" : "outlined"}
    />
  );
}

function AttachmentRow({ attachment }: { attachment: IngestAttachment }) {
  const navigate = useNavigate();
  const failed = attachment.processing_status === "failed";

  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Description color="action" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: "break-all" }}>
              {attachment.file_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {attachment.mime_type} · {fmtSize(attachment.file_size_bytes)} · uploaded{" "}
              {fmtDate(attachment.created_at)}
            </Typography>
          </Box>
          <StatusChip status={attachment.processing_status} />
          {attachment.processing_status === "done" && attachment.derived_article_id && (
            <IconButton
              size="small"
              title="Open derived article"
              onClick={() => navigate(`/kb/${attachment.derived_article_id}`)}
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          )}
        </Box>
        {(attachment.processing_status === "pending" ||
          attachment.processing_status === "processing") && <LinearProgress sx={{ mt: 1 }} />}
        {failed && attachment.processing_error && (
          <Alert severity="error" icon={<ErrorIcon fontSize="small" />} sx={{ mt: 1 }}>
            {attachment.processing_error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

export default function KBIngest() {
  const navigate = useNavigate();
  const { data: attachments = [], isLoading } = useIngestAttachments();
  const uploadMutation = useIngestUpload();

  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      setError(null);
      const list = Array.from(files);
      for (const file of list) {
        if (file.size > MAX_BYTES) {
          setError(
            `${file.name} is ${fmtSize(file.size)} — over the 20 MB cap. Larger files coming in a future update.`,
          );
          continue;
        }
        try {
          await uploadMutation.mutateAsync(file);
        } catch (e) {
          setError(e instanceof Error ? e.message : "Upload failed");
        }
      }
    },
    [uploadMutation],
  );

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) {
      void handleFiles(e.dataTransfer.files);
    }
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(true);
  };

  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
  };

  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      void handleFiles(e.target.files);
      e.target.value = ""; // allow re-picking same file
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/kb")}>
          Back to KB
        </Button>
        <Typography variant="h4" sx={{ flex: 1 }}>
          Import to Knowledge Base
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Drop a PDF, image, audio, video, or text file. We'll send it to Gemini, get back structured
        markdown, and create a draft KB article. Files run through a Cloud Run job that polls every
        5 minutes — usually ready within a minute or two. Files capped at 20 MB for now.
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          p: 4,
          borderStyle: "dashed",
          borderWidth: 2,
          borderColor: dragActive ? "primary.main" : "divider",
          bgcolor: dragActive ? "action.hover" : "background.paper",
          textAlign: "center",
          mb: 3,
          transition: "all 0.15s",
        }}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      >
        <CloudUpload sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
        <Typography variant="body1" sx={{ mb: 1 }}>
          Drop files here, or
        </Typography>
        <Button
          variant="contained"
          component="label"
          startIcon={<CloudUpload />}
          disabled={uploadMutation.isPending}
        >
          {uploadMutation.isPending ? "Uploading…" : "Choose files"}
          <input type="file" hidden multiple accept={ACCEPT_ATTRIBUTE} onChange={onPickFiles} />
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
          Supports PDF, JPG/PNG/WEBP/HEIC, MP3/WAV/FLAC/M4A, MP4/MPEG/MOV/WEBM, TXT/MD
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Typography variant="h6" sx={{ mb: 1 }}>
        Recent uploads
      </Typography>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : attachments.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: "italic", py: 4, textAlign: "center" }}
        >
          Nothing uploaded yet.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {attachments.map((a) => (
            <AttachmentRow key={a.id} attachment={a} />
          ))}
        </Stack>
      )}
    </Box>
  );
}
